// A facet's editor (the Mac's FacetEditor): changes one rule of the contents' filter or of a collection in place — its
// values (any of, or all of), is or isn't, a character's level, numbers and words. The filter bar opens it under the
// chips; the collection dialog in a popover beside a rule.
import { useEffect, useRef } from 'react';
import type { EntityKind, OpenedProject } from '@writing-suite/core';
import type { SavedSearchRule } from '@writing-suite/core/plan';
import { readCommentCategories } from '@writing-suite/core/revise';
import { KIND_CHOICES, sceneKind } from '../store/binderFilter.ts';
import { cssColor } from './helpers.ts';
import { Dot } from './Menu.tsx';

/** Changes one facet in place: its values (any of, or all of), is or isn't, a character's level, numbers and words. */
export function FacetEditor({ rule, entityKind, project, tags, label, onChange, onRemove, onDone, id = 'facet-editor' }: {
  rule: SavedSearchRule;
  entityKind?: EntityKind | 'object-or-motif';
  project: OpenedProject;
  tags: readonly { tag: string; count: number }[];
  label: string;
  onChange: (rule: SavedSearchRule) => void;
  onRemove: () => void;
  onDone: () => void;
  id?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    ref.current?.querySelector<HTMLElement>('input:not([type="checkbox"]), [role="radio"][aria-checked="true"], input, button')?.focus();
  }, []);
  const set = (patch: Partial<SavedSearchRule>) => onChange({ ...rule, ...patch });
  const value = rule.values[0] ?? '';
  // Has (or hasn't) comments, suggestions.
  const hasPicker = (
    <Segmented label="Has or hasn’t" value={rule.negate ? 'not' : 'is'} choices={[['is', 'Has'], ['not', 'Hasn’t']]} onChange={(v) => set({ negate: v === 'not' })} />
  );
  const isPicker = (
    <Segmented label="Is or isn’t" value={rule.negate ? 'not' : 'is'} choices={[['is', 'Is'], ['not', 'Isn’t']]} onChange={(v) => set({ negate: v === 'not' })} />
  );
  const matchPicker = (allowsAll: boolean) => (
    <Segmented
      label="Match"
      value={rule.negate ? 'none' : rule.all ? 'all' : 'any'}
      choices={[['any', 'Any Of'], ...(allowsAll ? [['all', 'All Of'] as [string, string]] : []), ['none', 'None Of']]}
      onChange={(v) => set({ negate: v === 'none', all: v === 'all' })}
    />
  );
  const words = (placeholder = 'Words') => (
    <>
      <input type="text" className="facet-words" aria-label={placeholder} placeholder={placeholder} value={value} onChange={(e) => set({ values: e.target.value ? [e.target.value] : [] })} />
      <p className="facet-hint">Words match from their start; put "a phrase" in quotes.</p>
    </>
  );
  const fields = project.allFields();

  let controls: React.ReactNode;
  switch (rule.kind) {
    case 'field': {
      const field = fields.find((f) => f.id === rule.field);
      if (field?.type === 'enum') {
        controls = (
          <>
            {isPicker}
            <Checklist items={[...field.options.map((o) => ({ id: o.id, name: o.name, color: cssColor(o.color) })), { id: '', name: 'None' }]} selected={rule.values} onChange={(values) => set({ values })} />
          </>
        );
      } else if (field?.type === 'tags') {
        controls = (
          <>
            {matchPicker(true)}
            <Checklist items={[...tags.map((t) => ({ id: t.tag, name: `${t.tag}  ·  ${t.count}` })), { id: '', name: 'Untagged' }]} selected={rule.values} onChange={(values) => set({ values })} />
          </>
        );
      } else if (field?.type === 'entity-ref') {
        const kind = rule.field === 'setting' ? 'place' : 'character';
        const cast = project.record.entities.filter((e) => e.kind === kind).sort((a, b) => a.name.localeCompare(b.name));
        controls = (
          <>
            {isPicker}
            <Checklist items={[...cast.map((e) => ({ id: e.id, name: e.name, color: cssColor(e.color) })), { id: '', name: 'None' }]} selected={rule.values} onChange={(values) => set({ values })} />
          </>
        );
      } else if (field?.type === 'boolean') {
        controls = <Segmented label={field.name} value={value === 'false' ? 'false' : 'true'} choices={[['true', 'Yes'], ['false', 'No']]} onChange={(v) => set({ values: [v] })} />;
      } else {
        controls = (
          <>
            <label className="facet-check">
              <input type="checkbox" checked={rule.op === 'empty'} onChange={(e) => set({ op: e.target.checked ? 'empty' : 'contains' })} /> Is empty
            </label>
            {rule.op !== 'empty' ? words() : null}
            {isPicker}
          </>
        );
      }
      break;
    }
    case 'entity': {
      const first = project.record.entities.find((e) => e.id === rule.values[0]);
      const kinds: EntityKind[] = entityKind === 'object-or-motif' ? ['object', 'motif'] : entityKind ? [entityKind] : first ? [first.kind] : ['character', 'place', 'object', 'motif'];
      const cast = project.record.entities.filter((e) => kinds.includes(e.kind) || rule.values.includes(e.id)).sort((a, b) => a.name.localeCompare(b.name));
      controls = (
        <>
          <Segmented
            label="Level"
            value={rule.level === 'pov' ? 'pov' : rule.level === 'on-stage' ? 'on-stage' : 'mentioned'}
            choices={[['pov', 'POV'], ['on-stage', 'On Stage'], ['mentioned', 'Mentioned']]}
            onChange={(v) => onChange({ ...rule, level: v === 'mentioned' ? undefined : (v as 'pov' | 'on-stage') })}
          />
          {matchPicker(true)}
          <Checklist items={cast.map((e) => ({ id: e.id, name: e.name, color: cssColor(e.color) }))} selected={rule.values} onChange={(values) => set({ values })} />
        </>
      );
      break;
    }
    case 'text': case 'title': case 'synopsis': case 'notes':
      controls = (
        <>
          {rule.kind === 'synopsis' || rule.kind === 'notes' ? (
            <label className="facet-check">
              <input type="checkbox" checked={rule.op === 'empty'} onChange={(e) => onChange({ ...rule, op: e.target.checked ? 'empty' : undefined })} /> Is empty
            </label>
          ) : null}
          {rule.op !== 'empty' ? words() : null}
          {isPicker}
        </>
      );
      break;
    case 'modified-within':
      controls = (
        <>
          <Segmented label="Changed" value={rule.negate ? 'not' : 'is'} choices={[['is', 'Changed'], ['not', 'Not Changed']]} onChange={(v) => set({ negate: v === 'not' })} />
          <label className="facet-row">
            In the last
            <input type="number" min={1} max={3650} value={rule.days ?? 30} onChange={(e) => set({ days: Math.min(3650, Math.max(1, Math.trunc(Number(e.target.value)) || 1)) })} />
            days
          </label>
        </>
      );
      break;
    case 'words': {
      const n = (v: string) => Math.max(0, Math.trunc(Number(v.replace(/[^0-9]/g, ''))) || 0);
      controls = (
        <>
          <Segmented label="Words" value={rule.op ?? 'lt'} choices={[['lt', 'Under'], ['gt', 'Over'], ['between', 'Between']]} onChange={(v) => onChange({ ...rule, op: v as 'lt' | 'gt' | 'between' })} />
          <label className="facet-row">
            <input type="text" inputMode="numeric" aria-label="Words" value={String(rule.number ?? 0)} onChange={(e) => set({ number: n(e.target.value) })} />
            {rule.op === 'between' ? (
              <>
                and
                <input type="text" inputMode="numeric" aria-label="Up to" value={String(rule.upper ?? rule.number ?? 0)} onChange={(e) => set({ upper: n(e.target.value) })} />
              </>
            ) : null}
            words
          </label>
        </>
      );
      break;
    }
    case 'target':
      controls = (
        <>
          <Segmented label="Target" value={value === 'reached' ? 'reached' : 'under'} choices={[['under', 'Under Target'], ['reached', 'Reached']]} onChange={(v) => set({ values: [v] })} />
          {isPicker}
        </>
      );
      break;
    case 'comments': {
      const categories = readCommentCategories(project.record);
      controls = (
        <>
          <label className="facet-row">
            Category
            <select value={rule.category ?? ''} onChange={(e) => onChange({ ...rule, category: e.target.value || undefined })}>
              <option value="">Any</option>
              {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              {categories.length ? <option value="none">No Category</option> : null}
            </select>
          </label>
          <label className="facet-row">
            From
            <select value={rule.author ?? ''} onChange={(e) => onChange({ ...rule, author: e.target.value || undefined })}>
              <option value="">Anyone</option>
              <option value="human">People</option>
              <option value="agent">Agents</option>
            </select>
          </label>
          {hasPicker}
        </>
      );
      break;
    }
    case 'kind': {
      const kinds = KIND_CHOICES.map((k) => (k.id === 'prose' ? { ...k, id: sceneKind(project) } : k));
      controls = (
        <>
          <Checklist items={kinds.map((k) => ({ id: k.id, name: k.name }))} selected={rule.values} onChange={(values) => set({ values })} />
          {isPicker}
        </>
      );
      break;
    }
    case 'suggestions':
      controls = hasPicker;
      break;
    case 'within': case 'included-in-compile': case 'locked':
      controls = isPicker;
      break;
    case 'unsupported':
      controls = <p className="facet-hint">This filter was made in a newer version of Five Rooms.</p>;
      break;
  }

  return (
    <div
      className="facet-editor"
      id={id}
      role="group"
      aria-label={`Change filter: ${label}`}
      ref={ref}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          e.stopPropagation();
          onDone();
        }
      }}
    >
      <p className="facet-title">{label}</p>
      {controls}
      <div className="facet-actions">
        <button type="button" className="link-button danger" onClick={onRemove}>
          Remove
        </button>
        <button type="button" className="quiet-button" onClick={onDone}>
          Done
        </button>
      </div>
    </div>
  );
}

export function Segmented({ label, value, choices, onChange }: { label: string; value: string; choices: [string, string][]; onChange: (v: string) => void }) {
  return (
    <div className="segmented small facet-segmented" role="radiogroup" aria-label={label}>
      {choices.map(([id, name]) => (
        <button key={id} type="button" role="radio" aria-checked={value === id} className={value === id ? 'on' : ''} onClick={() => onChange(id)}>
          {name}
        </button>
      ))}
    </div>
  );
}

/** Values to tick, each with its colour. */
export function Checklist({ items, selected, onChange }: { items: { id: string; name: string; color?: string | null }[]; selected: string[]; onChange: (values: string[]) => void }) {
  return (
    <ul className="facet-checklist">
      {items.map((item) => {
        const on = selected.includes(item.id);
        return (
          <li key={item.id}>
            <label>
              <input type="checkbox" checked={on} onChange={() => onChange(on ? selected.filter((v) => v !== item.id) : [...selected, item.id])} />
              {item.color !== undefined ? <Dot color={item.color} /> : null}
              <span>{item.name}</span>
            </label>
          </li>
        );
      })}
    </ul>
  );
}
